import React, { useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import api, { fileUrl, formatApiError } from "@/lib/apiClient";
import { toast } from "sonner";
import { Camera, QrCode, ScanLine, X, Loader2, Image as ImageIcon } from "lucide-react";

function extractCode(text) {
  const m = /\/eq\/([^/?#\s]+)/.exec(text || "");
  return (m ? m[1] : (text || "")).trim();
}

export default function EquipmentMediaFields({ qrCode, onQr, photo, onPhoto, qrDisabled = false }) {
  const [scanOpen, setScanOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const scannerRef = useRef(null);
  const containerId = "qr-reader-box";

  const startScan = async () => {
    setScanOpen(true);
    try {
      const { Html5Qrcode } = await import("html5-qrcode");
      await new Promise((r) => setTimeout(r, 200));
      const scanner = new Html5Qrcode(containerId);
      scannerRef.current = scanner;
      await scanner.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: 220 },
        async (decoded) => {
          const code = extractCode(decoded);
          onQr(code);
          toast.success(`QR lido: ${code}`);
          await stopScan();
        },
        () => {}
      );
    } catch (err) {
      toast.error("Não foi possível abrir a câmera. Verifique a permissão.");
      setScanOpen(false);
    }
  };

  const stopScan = async () => {
    try {
      if (scannerRef.current) { await scannerRef.current.stop(); scannerRef.current.clear(); }
    } catch { /* ignore */ }
    scannerRef.current = null;
    setScanOpen(false);
  };

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onPhoto(data.path);
      toast.success("Foto do aparelho enviada.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Falha no upload");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="space-y-1.5">
        <Label>Nº do QR / Patrimônio {qrDisabled && <span className="text-xs text-slate-400">(não editável)</span>}</Label>
        <div className="flex gap-2">
          <Input value={qrCode || ""} onChange={(e) => onQr(e.target.value)} disabled={qrDisabled}
            placeholder="Digite ou escaneie o número (ex: 10001)" data-testid="equipment-qrcode-input" className="flex-1" />
          <Button type="button" variant="outline" onClick={startScan} disabled={qrDisabled}
            className="gap-1.5 flex-shrink-0" data-testid="equipment-qr-scan-button">
            <ScanLine className="h-4 w-4" /> Escanear
          </Button>
        </div>
      </div>

      <div className="space-y-1.5">
        <Label className="flex items-center gap-1.5"><Camera className="h-4 w-4 text-[#1e7be0]" /> Foto real do aparelho *</Label>
        <div className="flex items-center gap-3 flex-wrap">
          <div className={`h-16 w-16 rounded-lg bg-slate-100 border flex items-center justify-center overflow-hidden flex-shrink-0 ${photo ? "border-slate-200" : "border-amber-300"}`}>
            {photo ? <img src={fileUrl(photo)} alt="Aparelho" className="h-full w-full object-cover" data-testid="equipment-photo-preview" />
              : uploading ? <Loader2 className="h-6 w-6 text-slate-400 animate-spin" /> : <QrCode className="h-6 w-6 text-slate-300" />}
          </div>
          <label className="cursor-pointer inline-flex items-center gap-1.5 rounded-md border border-slate-200 text-sm px-3 py-2 hover:bg-slate-50" data-testid="equipment-photo-camera-label">
            <Camera className="h-4 w-4" /> Tirar foto
            <input type="file" accept="image/*" capture="environment" className="hidden" onChange={uploadPhoto} disabled={uploading} data-testid="equipment-photo-camera-input" />
          </label>
          <label className="cursor-pointer inline-flex items-center gap-1.5 rounded-md border border-slate-200 text-sm px-3 py-2 hover:bg-slate-50" data-testid="equipment-photo-upload-label">
            <ImageIcon className="h-4 w-4" /> Galeria
            <input type="file" accept="image/*" className="hidden" onChange={uploadPhoto} disabled={uploading} data-testid="equipment-photo-input" />
          </label>
          {photo && <button type="button" onClick={() => onPhoto("")} className="text-red-500 text-sm" data-testid="equipment-photo-remove">Remover</button>}
        </div>
        <p className="text-xs text-slate-400">Obrigatória. A foto real substitui a ilustração padrão na área do cliente.</p>
      </div>

      <Dialog open={scanOpen} onOpenChange={(o) => { if (!o) stopScan(); }}>
        <DialogContent className="max-w-sm" data-testid="qr-scan-dialog">
          <DialogHeader><DialogTitle>Escanear QR do equipamento</DialogTitle></DialogHeader>
          <div id={containerId} className="rounded-lg overflow-hidden bg-black min-h-[260px]" />
          <p className="text-xs text-slate-500 text-center">Aponte a câmera para o QR da etiqueta.</p>
          <Button type="button" variant="outline" onClick={stopScan} className="gap-1.5"><X className="h-4 w-4" /> Cancelar</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
